import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton, Chip } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useLazyListIncidentReportsQuery } from "store/services/incidentReportApi";

import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";

import { useAppMeta, useTablePagination } from "hooks";

const getStatusStyle = (status) => {
  const styles = {
    PENDING: { backgroundColor: "#FDEDEC", textColor: "#E74C3C" },
    RESOLVED: { backgroundColor: "#EBF5FB", textColor: "green" },
  };

  return (
    styles[status?.toUpperCase()] || {
      backgroundColor: "#F8F9F9",
      textColor: "#7F8C8D",
    }
  );
};

const IncidentReportListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [
    {
      title: "Reports",
      path: "/app/crm/reports/list",
    },
    {
      title: "Incident Reports",
    },
  ];

  const incidentReportTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Incident ID",
      selector: (row) => row.incidentId,
      cell: (row) => (
        <Link
          title={row?.incidentId}
          to={`/app/crm/incident-report/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.incidentId ? (
            <HighlightedText text={row?.incidentId} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },

    {
      name: "Driver",
      selector: (row) => row.driver?.name,
      cell: (row) => (
        <Link
          title={row?.driver?.name}
          to={`/app/crm/driver/${row?.driver?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.driver?.name ? (
            <HighlightedText text={row?.driver?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },

    {
      name: "Vehicle",
      selector: (row) => row?.vehicle?.registration_number,
      cell: (row) => (
        <div>
          <span className="text-sm">
            {row?.vehicle?.registration_number || "N/A"}
          </span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.vehicle?.vehicle_id || "N/A"}
          </span>
        </div>
      ),
    },
    {
      name: "Type",
      selector: (row) => row.incidentType?.name,
      cell: (row) => row?.incidentType?.name ?? "N/A",
    },
    {
      name: "Date",
      selector: (row) => <AppTableDateTime date={row?.incidentDate} />,
      // cell: (row) => localizeDateTimeShort(row?.incidentDate),
    },
    {
      name: "Status",
      selector: (row) => row.status?.name,
      cell: (row) => {
        const statusName = row?.status?.name || "N/A";
        const { backgroundColor, textColor } = getStatusStyle(statusName);

        return (
          <Chip
            label={statusName}
            size="small"
            sx={{
              backgroundColor,
              color: textColor,
              fontWeight: 500,
              textTransform: "capitalize",
            }}
          />
        );
      },
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/incident-report/${row._id}/detail`}>
            <IconButton size="small" aria-label="View Incident" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>

          <Link to={`/app/crm/incident-report/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Incident">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchIncidentReports,
    { data: incidentReportRes, isLoading, isFetching, error },
  ] = useLazyListIncidentReportsQuery();

  React.useEffect(() => {
    fetchIncidentReports({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchIncidentReports, keyword, perPage]);

  useAppMeta(`${activeTab} Incident Reports`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={incidentReportTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Search Incident Reports"
          disabled={isLoading || isFetching}
        ></TableFilter>

        <AppTable
          columns={columns}
          data={incidentReportRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={incidentReportRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default IncidentReportListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/incident-report/create">
      <Button startIcon={<Add />} variant="contained">
        New Incident Report
      </Button>
    </Link>
  );
};
